import ModelTypeConstants from 'constants/ModelTypeConstants';
import {StructuredTableEditStatus} from 'notebook/structured-tables/action-functions/toggle-edit-mode/reduceToggledTableEditState';
import * as StructuredTableTogglableStateActions from 'notebook/structured-tables/action-functions/toggle-edit-mode/StructuredTableTogglableStateActions';
import GenericStructuredTableAdapter from 'notebook/structured-tables/GenericStructuredTableAdapter';
import {
  AxisContextMenuActionFunctionParameters,
  RowContextMenuActionFunction,
  StructuredTable,
  StructuredTableContextMenuItem,
  StructuredTableRow,
} from 'notebook/structured-tables/StructuredTableConstants';
import {SelectionRange, TableDimension} from 'notebook/tables/TableConstants';
import getDimensionalOperationText from 'notebook/tables/util/getDimensionalOperationText';
import APIUtil from 'util/APIUtil';

/**
 * Helper for making an action function to archive or unarchive a row in your structured table.
 */

interface MakeArchiveRowActionFunctionConfigurationParams<TTableMetadata, TRowMetadata> {
  allowOutsideOfEntry?: boolean;
  getSelectedItems: (
    structuredTable: StructuredTable<TTableMetadata, TRowMetadata>,
    selectedRows: Array<StructuredTableRow<TRowMetadata>>,
    adapter: GenericStructuredTableAdapter<TTableMetadata, TRowMetadata>
  ) => Array<{id: string; modelType: ModelTypeConstants}>;
  onSelectForArchive: (
    actionFunctionParams: AxisContextMenuActionFunctionParameters<TTableMetadata, TRowMetadata>,
    items: Array<{modelType: ModelTypeConstants; id: string}>
  ) => void;
  onSelectForUnarchive: (
    actionFunctionParams: AxisContextMenuActionFunctionParameters<TTableMetadata, TRowMetadata>,
    items: Array<{modelType: ModelTypeConstants; id: string}>
  ) => void;
}

function shouldShowMenuItem<TTableMetadata, TRowMetadata>(
  configurationParams: MakeArchiveRowActionFunctionConfigurationParams<TTableMetadata, TRowMetadata>,
  actionFunctionParams: AxisContextMenuActionFunctionParameters<TTableMetadata, TRowMetadata>
): boolean {
  return (
    // Some rows are selected
    actionFunctionParams.selectionRange != null &&
    // The table is not read-only
    !actionFunctionParams.locked &&
    // The table is in a notebook entry (or non-entry action function is explicitly allowed)
    (configurationParams.allowOutsideOfEntry || actionFunctionParams.entryOrTemplateId != null) &&
    // The table is not in a template
    (configurationParams.allowOutsideOfEntry ||
      APIUtil.isBaseEntry(actionFunctionParams.entryOrTemplateId!)) && // The table is not a togglable-state table that is currently being edited
    StructuredTableTogglableStateActions.getStructuredTableTogglableStatus(
      actionFunctionParams.structuredTable.cid
    ) !== StructuredTableEditStatus.EDITING
  );
}

function getSelectedRowIndices(selectionRange: SelectionRange): Array<number> {
  const numSelectedRows = selectionRange.maxIndex - selectionRange.minIndex + 1;
  return new Array(numSelectedRows).fill(null).map((_, i) => selectionRange.minIndex + i);
}

function getRowAxisContextMenuItem<TTableMetadata, TRowMetadata>(
  configurationParams: MakeArchiveRowActionFunctionConfigurationParams<TTableMetadata, TRowMetadata>,
  actionFunctionParams: AxisContextMenuActionFunctionParameters<TTableMetadata, TRowMetadata>
): StructuredTableContextMenuItem | null {
  if (!shouldShowMenuItem(configurationParams, actionFunctionParams)) {
    return null;
  }

  const adapter = actionFunctionParams.structuredTableAdapter as unknown as GenericStructuredTableAdapter<
    TTableMetadata,
    TRowMetadata
  >;
  const selectedRows: Array<StructuredTableRow<TRowMetadata>> =
    actionFunctionParams.structuredTable.rows.slice(
      actionFunctionParams.selectionRange!.minIndex,
      actionFunctionParams.selectionRange!.maxIndex + 1
    );

  const archiveItems = configurationParams.getSelectedItems(
    actionFunctionParams.structuredTable,
    selectedRows,
    adapter
  );

  const anySelectedRowsWithoutArchiveItem = selectedRows.length > archiveItems.length;
  if (anySelectedRowsWithoutArchiveItem) {
    // It doesn't make sense to archive/unarchive a row that doesn't have an associated item.
    // As a UX choice, we disable the option (rather than making it only act on a subset of rows).
    return null;
  }

  const selectedRowIndices = getSelectedRowIndices(actionFunctionParams.selectionRange!);

  // liveRowMetadataByRow[].archivePurpose may be `undefined` if the archive purpose hasn't been fetched yet.
  // For simplicity, we treat `undefined` the same as `null` and assume the row is unarchived, even though
  // this might not be accurate during the few seconds before the fetch completes.
  const areAllSelectedRowsArchived = selectedRowIndices.every(
    (idx) => actionFunctionParams.liveRowMetadataByRow?.[idx]?.archivePurpose != null
  );
  const areAllSelectedRowsUnarchived = selectedRowIndices.every(
    (idx) => actionFunctionParams.liveRowMetadataByRow?.[idx]?.archivePurpose == null
  );

  if (areAllSelectedRowsUnarchived) {
    return {
      name: getDimensionalOperationText({
        verb: 'Archive',
        selectionRange: actionFunctionParams.selectionRange,
        dimension: TableDimension.ROW,
      }),
      onSelect: () => {
        configurationParams.onSelectForArchive(actionFunctionParams, archiveItems);
      },
    };
  } else if (areAllSelectedRowsArchived) {
    return {
      name: getDimensionalOperationText({
        verb: 'Unarchive',
        selectionRange: actionFunctionParams.selectionRange,
        dimension: TableDimension.ROW,
      }),
      onSelect: () => {
        configurationParams.onSelectForUnarchive(actionFunctionParams, archiveItems);
      },
    };
  } else {
    // As a UX choice, we disable the option if the selection does not consist entirely of either archived
    // rows or unarchived rows.
    return null;
  }
}

export default function makeArchiveRowActionFunction<TTableMetadata, TRowMetadata>(
  configurationParams: MakeArchiveRowActionFunctionConfigurationParams<TTableMetadata, TRowMetadata>
): RowContextMenuActionFunction<TTableMetadata, TRowMetadata> {
  return {
    getRowAxisContextMenuItem: (
      actionFunctionParams: AxisContextMenuActionFunctionParameters<TTableMetadata, TRowMetadata>
    ) => getRowAxisContextMenuItem(configurationParams, actionFunctionParams),
  };
}
